/**
 * ReportEntryDetail — the detail screen for an older `ReportEntry` row: its
 * included items and the approve-and-file button, with the client confirm.
 * Reachable only when ReportsView is given `reports`; the app passes none.
 */

import { BackLink } from "./BackLink";
import { rowMeta, titleOf } from "../reports/report-labels";
import type { ReportEntry, ReportSidecar } from "../reports/report-types";

export interface ReportEntryDetailProps {
  detail: ReportEntry;
  sidecar: ReportSidecar | null;
  confirmOpen: boolean;
  setConfirmOpen: (open: boolean) => void;
  closeDetail: () => void;
  doSend: (entry: ReportEntry) => Promise<void>;
  onSendClick: (entry: ReportEntry) => void;
  onOpenItem?: (capture_id: string) => void;
}

export function ReportEntryDetail({
  detail,
  sidecar,
  confirmOpen,
  setConfirmOpen,
  closeDetail,
  doSend,
  onSendClick,
  onOpenItem,
}: ReportEntryDetailProps) {
  return (
    <div data-testid="report-detail">
      <BackLink label="Reports" testid="report-detail-back" onBack={closeDetail} />
      <p class="report-detail-title" data-testid="report-detail-title">{titleOf(detail)}</p>
      <p class="report-detail-meta" data-testid="report-detail-meta">
        {(() => {
          const parts = [rowMeta(detail)];
          if (sidecar) {
            const n = sidecar.included.length;
            parts.push(`${n} item${n === 1 ? "" : "s"}`);
          }
          if (detail.report_kind === "daily" && sidecar?.weather) parts.push(sidecar.weather);
          return parts.join(" · ");
        })()}
      </p>

      <div class="report-detail-included">
        <p class="save-chip-group-hint">Included items — flagged at save</p>
        <ul data-testid="report-detail-items">
          {(sidecar?.included ?? []).map((it) => (
            <li key={it.capture_id} data-testid={`report-item-${it.capture_id}`}>
              <button type="button" class="report-item-open" onClick={() => onOpenItem?.(it.capture_id)}>
                <span class="report-item-title">{it.title}</span>
                <span class="report-item-meta">{it.meta}</span>
                <span class="report-item-chevron" aria-hidden="true">›</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* §4.7, LOCKED: a client-facing report is never one tap. Red is adding a
          further gate here once the PWA is fully working, so this stays a
          two-step SEAM rather than a one-off inline check. */}
      {confirmOpen && (
        <div class="report-send-confirm" data-testid="report-send-confirm-panel">
          <p class="report-send-confirm-q">File this as the CLIENT report?</p>
          <button type="button" data-testid="report-send-confirm" onClick={() => void doSend(detail)}>
            Yes, file it
          </button>
          <button type="button" data-testid="report-send-cancel" onClick={() => setConfirmOpen(false)}>
            Cancel
          </button>
        </div>
      )}

      {/* "File", not "send". Mark writes the PDF into a folder and a human
          attaches it to an email (Red, 2026-07-27) — nothing is delivered to
          anyone by tapping this. Calling it "send" would tell a superintendent
          the client has it, which is exactly the kind of quiet untruth rule #10
          and §4.11 exist to prevent. Wiring Mark to actually send is future
          work; the label changes when the behaviour does. */}
      <button
        type="button"
        data-testid="report-send"
        disabled={sidecar?.sent === true}
        onClick={() => onSendClick(detail)}
      >
        {sidecar?.sent === true
          ? "Filed"
          : detail.report_kind === "client"
            ? "Approve and file as client report"
            : "Approve and file"}
      </button>
    </div>
  );
}
